import { useState, useCallback, useEffect, useMemo } from 'react';

type PositionData = {
  rowIndex: number;
  cellIndex: number;
  timeInMinutes: number;
};

type UseTableCellTimeTrackingResult = {
  cellTimeData: PositionData | null;
  setTableRef: (element: HTMLTableElement | null) => void;
};

export const useTableCellTimeTracking = (): UseTableCellTimeTrackingResult => {
  const [cellTimeData, setCellTimeData] = useState<PositionData | null>(null);
  const [tableRef, setTableRef] = useState<HTMLTableElement | null>(null); // Using state for tableRef

  const updatePositionData = useCallback(
    (event: MouseEvent) => {
      if (!tableRef) return;

      const target = (event.target as HTMLElement).closest('td') as HTMLTableCellElement | null;
      if (!target) return;

      const tdElement = target;
      const trElement = tdElement.parentElement as HTMLTableRowElement;
      const rowIndex = Array.from(trElement.parentElement?.children || []).indexOf(trElement);
      const cellIndex = Array.from(trElement.children).indexOf(tdElement);

      const { top, height } = tdElement.getBoundingClientRect();
      const mouseY = event.clientY - top;
      const timeInMinutes = Math.floor((mouseY / height) * 12) * 5;

      setCellTimeData({ rowIndex, cellIndex, timeInMinutes });
    },
    [tableRef]
  );

  useEffect(() => {
    if (!tableRef) return undefined;

    tableRef.addEventListener('mousemove', updatePositionData);

    return () => {
      tableRef.removeEventListener('mousemove', updatePositionData);
    };
  }, [tableRef, updatePositionData]);

  // This allows the conditional setting/use of the hook
  const refCallback = useCallback((element: HTMLTableElement | null) => {
    setTableRef(element);
  }, []);

  // Memoise the return object to prevent unnecessary re-renders in consuming components
  return useMemo(
    () => ({
      cellTimeData,
      setTableRef: refCallback
    }),
    [cellTimeData, refCallback]
  );
};
